<template>
  <div 
    :class="checkboxClasses" 
    class="base-checkbox" 
    v-on="$listeners"
  >
    <div 
      class="base-checkbox__container" 
      @click.stop="toggleCheckbox"
    >
      <input
        :id="id || uniqueId"
        :name="name"
        :value="value"
        :disabled="isDisabled"
        :checked="isSelected"
        type="checkbox"
        @change="toggleCheckbox"
      >
    </div>

    <label
      v-if="$slots.default"
      :for="id || uniqueId"
      class="base-checkbox__label"
      @click.stop
    >
      <slot />
    </label>
  </div>
</template>

<script>
export default {
  model: {
    prop: "model",
    event: "change",
  },
};
</script>

<script setup>
import { ref, computed, watch, onMounted } from "vue";

const emit = defineEmits(["change"]);

const props = defineProps({
  id: {
    type: String,
    default: undefined,
  },
  model: {
    type: [String, Array, Boolean],
    default: undefined,
  },
  isChecked: {
    type: Boolean,
    default: undefined,
  },
  value: {
    type: String,
    default: undefined,
  },
  name: {
    type: String,
    default: "",
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
});

const uniqueId = ref("");

const isSelected = computed(() => {
  if (props.model === undefined) {
    return props.isChecked;
  } else if (Array.isArray(props.model)) {
    return props.model.includes(props.value);
  } else {
    return props.model;
  }
});

const checkboxClasses = computed(() => ({
  "base-checkbox--is-disabled": props.isDisabled,
  "base-checkbox--is-selected": isSelected.value,
}));

const toggleCheckbox = () => {
  if (props.isDisabled) {
    return;
  }

  let value = props.model;

  if (Array.isArray(value)) {
    value = value.slice();
    const index = value.indexOf(props.value);

    if (index === -1) {
      value.push(props.value);
    } else {
      value.splice(index, 1);
    }
  } else {
    value = !isSelected.value;
  }

  emit("change", value);
};

const getUniqueId = () => {
  if (props.id === undefined || typeof props.id !== 'string') {
    uniqueId.value = `base-checkbox--${Math.random()
      .toString(36)
      .substring(2, 10)}`;
  } else {
    uniqueId.value = props.id;
  }
};

// Watch for external isChecked changes
watch(
  () => props.isChecked,
  (newVal) => {
    if (newVal !== isSelected.value) {
      toggleCheckbox();
    }
  }
);

onMounted(() => {
  getUniqueId();

  // Sync initial isChecked state
  if (props.isChecked && !isSelected.value) {
    toggleCheckbox();
  }
});
</script>

<style lang="scss">
@import "./base-checkbox.scss";
</style>
